import { useState, useEffect, useCallback } from "react";
import AdminLayout from "./AdminLayout";
import { api } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Search, Filter, Eye, Loader2 } from "lucide-react";
import { Input } from "../../components/ui/input";

const AdminSurveys = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  const fetchSurveys = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      const response = await api.get(`/admin/surveys?${params.toString()}`);
      setSurveys(response.data.surveys);
    } catch {
      toast.error("Failed to load surveys");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const handleUpdateStatus = async (surveyId, newStatus) => {
    try {
      await api.put(`/admin/surveys/${surveyId}?status=${newStatus}`);
      toast.success("Survey updated successfully");
      fetchSurveys();
      setSelectedSurvey(null);
    } catch (error) {
      toast.error("Failed to update survey");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      requested: "bg-yellow-100 text-yellow-700",
      confirmed: "bg-blue-100 text-blue-700",
      completed: "bg-green-100 text-green-700",
      cancelled: "bg-red-100 text-red-700"
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  const statuses = [
    { value: "requested", label: "Requested" },
    { value: "confirmed", label: "Confirmed" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" }
  ];

  const filteredSurveys = surveys.filter(survey => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      survey.customer?.name?.toLowerCase().includes(search) ||
      survey.customer?.email?.toLowerCase().includes(search)
    );
  });

  return (
    <AdminLayout title="Surveys">
      {/* Filters */}
      <div className="bg-white border border-slate-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="surveys-search"
            />
          </div>
          <div className="sm:w-48">
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="surveys-status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Surveys Table */}
      <div className="bg-white border border-slate-200 overflow-hidden">
        {loading && (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF5A1F]" />
          </div>
        )}
        {!loading && filteredSurveys.length === 0 && (
          <div className="p-8 text-center text-slate-500">No surveys found</div>
        )}
        {!loading && filteredSurveys.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Quote</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Requested Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredSurveys.map((survey) => (
                  <tr key={survey.survey_id} className="hover:bg-slate-50">
                    <td className="px-4 py-4">
                      <div className="font-medium text-[#0F172A]">{survey.customer?.name || "N/A"}</div>
                      <div className="text-sm text-slate-500">{survey.customer?.email}</div>
                    </td>
                    <td className="px-4 py-4">
                      {survey.quote && (
                        <div className="text-sm">
                          <div>{survey.quote.from_postcode} → {survey.quote.to_postcode}</div>
                          <div className="text-slate-500">{survey.quote.bedrooms} bed {survey.quote.property_type}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm">{survey.confirmed_date || survey.requested_date}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm">{survey.requested_time}</div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 text-xs font-medium ${getStatusColor(survey.status)}`}>
                        {survey.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedSurvey(survey)}
                        data-testid={`view-survey-${survey.survey_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Survey Detail Modal */}
      <Dialog open={!!selectedSurvey} onOpenChange={() => setSelectedSurvey(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Survey Details</DialogTitle>
          </DialogHeader>
          
          {selectedSurvey && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="bg-slate-50 p-4">
                <h3 className="font-semibold text-[#0F172A] mb-2">Customer</h3>
                <div className="text-sm space-y-1">
                  <div><strong>Name:</strong> {selectedSurvey.customer?.name}</div>
                  <div><strong>Email:</strong> {selectedSurvey.customer?.email}</div>
                  <div><strong>Phone:</strong> {selectedSurvey.customer?.phone}</div>
                </div>
              </div>

              {/* Survey Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-500 uppercase mb-1">Requested Date</div>
                  <div className="font-medium">{selectedSurvey.requested_date}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase mb-1">Time Slot</div>
                  <div className="font-medium">{selectedSurvey.requested_time}</div>
                </div>
              </div>

              {/* Quote Info */}
              {selectedSurvey.quote && (
                <div className="bg-[#0F172A] p-4 text-white">
                  <h3 className="font-semibold mb-2">Related Quote</h3>
                  <div className="text-sm space-y-1">
                    <div>{selectedSurvey.quote.from_postcode} → {selectedSurvey.quote.to_postcode}</div>
                    <div>{selectedSurvey.quote.bedrooms} bed {selectedSurvey.quote.property_type}</div>
                    <div>Move date: {selectedSurvey.quote.move_date}</div>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedSurvey.surveyor_notes && (
                <div>
                  <div className="text-xs text-slate-500 uppercase mb-2">Notes</div>
                  <div className="text-sm text-slate-600">{selectedSurvey.surveyor_notes}</div>
                </div>
              )}

              {/* Status Update */}
              <div>
                <label className="text-xs text-slate-500 uppercase mb-2 block">Update Status</label>
                <Select 
                  defaultValue={selectedSurvey.status}
                  onValueChange={(v) => handleUpdateStatus(selectedSurvey.survey_id, v)}
                >
                  <SelectTrigger data-testid="update-survey-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminSurveys;
